import { REGION_TAG_LABELS } from '../facets/region-tag-label-data';

/**
 * Pure slug util for Finland's 18 admin1 (maakunta) regions. Deliberately no imports from
 * pages/ - a follow-up agent builds the pretty `/locations/{region-slug}` routes on top of
 * this map, this file only owns the slug shape.
 */

const LABELS = REGION_TAG_LABELS;

/**
 * ASCII-folds diacritics (a with a combining diaeresis for "a", etc.), lowercases, and
 * hyphenates a region display name into a URL-safe slug. Same fold technique as
 * utils/location/to-location-slug.ts, hyphen separator instead of underscore (regions read
 * as a pretty path segment, not a legacy location-slug).
 */
export function regionSlug(name: string): string {
  if (!name) return '';
  let s = name.normalize('NFD').replace(/[̀-ͯ]/g, '');
  s = s.toLowerCase();
  s = s.replace(/[^a-z0-9]+/g, '-');
  s = s.replace(/^-+|-+$/g, '');
  return s;
}

export interface RegionSlugTriple {
  en: string;
  fi: string;
  sv: string;
}

/**
 * Finland admin1 (maakunta) placeId -> its {en, fi, sv} slug triple, built from the same
 * checked-in gazetteer label seed data region-tag-labels.ts is built from. Includes Åland (as of
 * 2026-09-28): a synthetic admin1 entry, since GeoNames splits the region into two subdivisions
 * with no single geonameid of its own -- see generate-region-tag-labels.ts's FINLAND_ADMIN1_NAMES
 * comment on admin1Code '21' for how it was constructed.
 */
export const FINLAND_REGION_SLUGS: Record<string, RegionSlugTriple> = (() => {
  const map: Record<string, RegionSlugTriple> = {};
  for (const [placeId, entry] of Object.entries(LABELS)) {
    if (entry.type !== 'admin1') continue;
    map[placeId] = {
      en: regionSlug(entry.en),
      fi: regionSlug(entry.fi),
      sv: regionSlug(entry.sv),
    };
  }
  return map;
})();

export type RegionSlugLocale = keyof RegionSlugTriple;

let slugToPlaceId: Map<string, string> | null = null;

/**
 * Reverse lookup: a maakunta URL slug (any locale) -> its gazetteer placeId. Undefined for an
 * unrecognized slug, so route parsing can fall back to a plain, noindexed SRP rather than 404ing
 * (see the location-slug policy in docs/knowledge-graph/01-job-search-discovery.md).
 */
export function findFinlandRegionPlaceIdBySlug(locale: RegionSlugLocale, slug: string): string | undefined {
  if (!slugToPlaceId) {
    slugToPlaceId = new Map();
    for (const [placeId, byLocale] of Object.entries(FINLAND_REGION_SLUGS)) {
      for (const loc of ['en', 'fi', 'sv'] as RegionSlugLocale[]) {
        slugToPlaceId.set(`${loc}:${byLocale[loc]}`, placeId);
      }
    }
  }
  return slugToPlaceId.get(`${locale}:${slug.trim().toLowerCase()}`);
}

/** True when `placeId` is one of the 18 Finnish maakunta (admin1) gazetteer placeIds. */
export function isFinlandRegionPlaceId(placeId: string): boolean {
  return Object.prototype.hasOwnProperty.call(FINLAND_REGION_SLUGS, placeId);
}
